import { ErrorMessage } from '@hookform/error-message';
import { uploadData } from 'aws-amplify/storage';
import { forwardRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Controller } from "react-hook-form";
import { FaRegFile, FaRegFolder, FaRegFolderOpen, FaTimes } from "react-icons/fa";
import styled from "styled-components";
import { v4 as uuid } from 'uuid';
import theme from "../common/theme";
import displaySize from "../utils/displaySize";
import { ButtonBase } from "./Buttons";
import Progress from "./Progress";
import Row from "./Row";
import { P, Pmb } from "./Typography";

const Input = styled.input`
    flex: 1;
    outline: none;
    padding: 0.5rem;
    font-size: 1rem;
    border: 1px solid #ccc;
`

const Container = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    margin: 1rem 0;
`

const DragContainer = styled.div`
    background: ${theme.palette.light};
    border: 2px dotted ${props => props.$active ? 'green' : theme.palette.dark};
    padding: 2rem 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    font-weight: 700;

    span {
        color: ${theme.palette.dark};
        text-decoration: underline;
    }
`

const DragActive = () => (
    <DragContainer $active>
        <FaRegFolderOpen size='16px' />
        <Pmb>DROP FILE HERE</Pmb>
    </DragContainer>
)

const DragInActive = () => (
    <DragContainer>
        <FaRegFolder size='16px' />
        <Pmb>DRAG OR <span>CHOOSE FILE</span></Pmb>
    </DragContainer>
)

export const UncontrolledDropZone = forwardRef(({ accept = { 'application/pdf': ['.pdf'] }, errors, label, containerStyle, onChange, value, ...rest }, ref) => {
    const [progress, setProgress] = useState(null);
    const [fileMeta, setFileMeta] = useState({});

    const onDrop = async ([file]) => {
        const filename = `${uuid()}.pdf`;
        await uploadData({ path: `public/${filename}`, data: file, options: { contentType: 'application/pdf', onProgress: setProgress } }).result;
        setFileMeta(file);
        onChange(filename);
    }
    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept, maxFiles: 1, multiple: false });

    const renderDragArea = () => {
        if (value) {
            return null;
        }
        return (
            <>
                {isDragActive ? <DragActive /> : <DragInActive />}
                {progress && <Progress progress={(progress.transferredBytes / progress.totalBytes) * 100} />}
            </>
        )
    }

    const renderValue = () => {
        if (value) {
            return (
                <Row style={{ background: theme.palette.light, padding: '1rem' }}>
                    <FaRegFile />
                    <div style={{ flex: 1 }}>
                        <Pmb style={{ textTransform: 'uppercase', fontWeight: 700 }}>{fileMeta.name}</Pmb>
                        <P>{displaySize(fileMeta.size)}</P>
                    </div>
                    <ButtonBase onClick={(e) => {
                        e.stopPropagation();
                        setProgress(null);
                        onChange(null);
                    }}>
                        <FaTimes color="#000" />
                    </ButtonBase>
                </Row>
            )
        }
    }


    return (
        <Container style={containerStyle} {...getRootProps()}>
            <Input
                ref={ref}
                {...getInputProps()}
                {...rest}
            />
            {renderDragArea()}
            {renderValue()}
            {errors && <ErrorMessage
                errors={errors}
                name={rest.name}
                render={({ message }) => <P style={{ color: 'red' }}>{message}</P>}
            />}
        </Container>
    )
})

export default ({ name, control, rules, ...rest }) => {
    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field: { ...fieldProps } }) => (
                <UncontrolledDropZone {...fieldProps} {...rest} />
            )}
        />
    );
};
